import { css } from '@emotion/core';
import { Select } from 'antd';
import { withRouter } from 'next/router';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setCurrentPage, setGroupBy } from '@/src/compliance/redux/complianceReducer';
import {
  getChartsArrangedByState,
  getChartsArrangedByTopicAndState,
  getPaginatedCharts,
} from '@/src/compliance/redux/selectors';
import parseURL from '@/src/compliance/utils/parseURL';
import { generateHTMLString } from '@/src/pdf/templates/compliance';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import Loading from '@/src/shared/components/LoadingResults';
import Pagination from '@/src/shared/components/Pagination';
import { bp, colors, space } from '@/styles';
import { getCurrentDate } from '@/utils/dateTimeHelpers';

import PageButtons from './PageButtons';
import SearchResultsAccordion from './SearchResultsAccordion';

const { m } = space;

const style = {
  pageButtons: css`
    background-color: ${colors.white};
  `,
  paginationRow: css`
    display: flex;
    flex-wrap: wrap;
  `,
  btnRow: css`
    display: flex;
    justify-content: space-between;
  `,
  groupSelect: css`
    width: 100%;

    @media ${bp.sm} {
      width: 35%;
      padding-left: 16px;
    }
  `,
};

const SearchResults = props => {
  const dispatch = useDispatch();

  const {
    searchResults,
    searchFiltersValues = {},
    searchOptions = {},
  } = useSelector(state => state.compliance);

  const {
    charts: chartsOptions,
    states: statesOptions,
    categories: categoriesOptions,
  } = searchOptions;

  const {
    searchResults: results = {},
    currentPage,
    isFetchingCharts,
    groupByChart,
  } = searchResults;

  const charts = results.charts || results.Charts || [];

  const formattedCharts = getChartsArrangedByTopicAndState(charts);
  const formattedChartsByState = getChartsArrangedByState(charts);
  const resultsGroupedByState = getChartsArrangedByState(charts);

  const extraSearchValues = {
    count: formattedCharts.length,
    results: getPaginatedCharts(formattedCharts, currentPage),
    originalResultsFormat: charts,
    stateCount: formattedChartsByState.length,
  };

  const { count, results: paginatedResults, originalResultsFormat, stateCount } = extraSearchValues;

  const [pdfExportStatus, setPdfExportStatus] = useState(false);

  const { router, displayChanges } = props;

  const handlePageChange = pageNumber => {
    dispatch(setCurrentPage(pageNumber));
  };

  const handleGroupBy = e => {
    const groupVal = Boolean(e === 'chart');
    dispatch(setGroupBy(groupVal));
  };

  const handleExportToPdf = async () => {
    setPdfExportStatus(true);

    const updatedPayload = parseURL(searchOptions, router);

    const getValue = (stateValue, urlValue) => {
      if (Array.isArray(stateValue)) {
        return stateValue.length ? stateValue : urlValue;
      }

      return stateValue || urlValue;
    };

    const categoryValue = getValue(searchFiltersValues.categories, updatedPayload.categories);
    const chartValues = getValue(searchFiltersValues.charts, updatedPayload.charts) || [];
    const stateValues = getValue(searchFiltersValues.states, updatedPayload.states) || [];

    const categoryIds = Array.isArray(categoryValue)
      ? categoryValue.map(Number)
      : categoryValue
        ? [Number(categoryValue)]
        : [];

    const chartIds = Array.isArray(chartValues)
      ? chartValues.map(Number)
      : chartValues
        ? [Number(chartValues)]
        : [];

    const stateCodes = Array.isArray(stateValues)
      ? stateValues
      : stateValues
        ? [stateValues]
        : [];

    const selectedCategories = Array.isArray(categoriesOptions)
      ? categoriesOptions.filter(cat =>
          categoryIds.includes(Number(cat.categoryId || cat.CategoryId))
        )
      : [];

    const selectedCharts = Array.isArray(chartsOptions)
      ? chartsOptions.filter(chart =>
          chartIds.includes(Number(chart.chartId || chart.ChartId))
        )
      : [];

    const selectedStates = Array.isArray(statesOptions)
      ? statesOptions.filter(state =>
          stateCodes.includes(state.stateCode || state.StateCode)
        )
      : [];

    const searchCriteria = {
      ...updatedPayload,
      ...searchFiltersValues,
      categories: selectedCategories,
      charts: selectedCharts,
      states: selectedStates,
      searchText: searchFiltersValues.searchText || updatedPayload.searchText || '',
      dateStart: searchFiltersValues.dateStart || updatedPayload.dateStart || null,
      dateEnd: searchFiltersValues.dateEnd || updatedPayload.dateEnd || null,
    };

    const currentDate = getCurrentDate();
    const fileName = `Compliassist Charts - ${currentDate}.pdf`;
    const groupBy = groupByChart ? 'chart' : 'state';

    const html = generateHTMLString(
      formattedCharts,
      formattedChartsByState,
      searchCriteria,
      groupBy,
      displayChanges
    );

    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  };

  const pagination = (
    <Pagination
      current={currentPage}
      total={groupByChart ? count : stateCount}
      pageSize={10}
      onChange={pageNumber => {
        handlePageChange(pageNumber);
      }}
      css={css`
        margin-bottom: 24px;
      `}
    />
  );

  return (
    <div>
      <div css={style.paginationRow}>
        {pagination}

        <Select
          defaultValue="chart"
          css={[style.groupSelect]}
          onChange={handleGroupBy}
          value={groupByChart ? 'chart' : 'state'}
        >
          <Select.Option value="chart">Group By Chart</Select.Option>
          <Select.Option value="state">Group By State</Select.Option>
        </Select>
      </div>

      <PageButtons
        isDisabled={Boolean(count)}
        data={originalResultsFormat}
        pdfDownloading={pdfExportStatus}
        pdfDisabled={isFetchingCharts}
        handleExportToPdf={handleExportToPdf}
        groupBy={groupByChart ? 'chart' : 'state'}
        displayChanges={displayChanges}
      />

      <div css={m.t24}>
        {isFetchingCharts ? (
          <Loading height="500px" />
        ) : (
          <SearchResultsAccordion
            results={groupByChart ? paginatedResults : resultsGroupedByState}
            groupByChart={groupByChart}
            displayChanges={displayChanges}
          />
        )}
      </div>

      <div
        css={css`
          margin-top: 24px;
        `}
      >
        {pagination}
      </div>
    </div>
  );
};

export default withRouter(SearchResults);